import { useEffect, useState } from 'react';
import clsx from 'clsx';
import type { OrderDetail, UpsellStatusEntry } from '../../lib/orders';
import { UpsellOndertapijtDialog } from './UpsellOndertapijtDialog';
import { UpsellVlekkenbaasDialog } from './UpsellVlekkenbaasDialog';
import { UpsellRandReparatieDialog } from './UpsellRandReparatieDialog';
import { NabestelOndertapijtVerzendenDialog } from './NabestelOndertapijtVerzendenDialog';
import type { UpsellType } from '../../lib/upsell';

const TERMINAL_STATUSES = new Set(['Teruggebracht', 'Geannuleerd', 'Afgerond']);
// Al afgeleverd (geen route meer) → ondertapijt kan alleen nog apart verzonden
// worden. 'Geannuleerd' valt hier bewust buiten (geen nabestelling).
const DELIVERED_STATUSES = new Set(['Teruggebracht', 'Afgerond']);
const STORAGE_KEY = 'office-app:order-detail:upsell-open';

interface UpsellSectionProps {
  order: OrderDetail;
  onRefresh: () => void;
}

function StatusChip({ ordered, bron }: UpsellStatusEntry) {
  if (!ordered) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
      Besteld{bron ? ` via ${bron}` : ''}
    </span>
  );
}

interface CardProps {
  title: string;
  description: string;
  status: UpsellStatusEntry;
  disabled: boolean;
  disabledReason: string | null;
  onClick: () => void;
  orderedLabel?: string;
  /** Forceert het knoplabel ongeacht de besteld-status (bv. 'Nabestellen + verzenden'). */
  ctaLabel?: string;
}

function UpsellCard({ title, description, status, disabled, disabledReason, onClick, orderedLabel, ctaLabel }: CardProps) {
  const label = ctaLabel
    ?? (status.ordered ? (orderedLabel ?? 'Al toegevoegd') : 'Toevoegen');
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-900">{title}</h3>
        <StatusChip {...status} />
      </div>
      <p className="text-xs text-slate-500 flex-1">{description}</p>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? disabledReason || '' : undefined}
        className={clsx(
          'text-xs font-medium px-2.5 py-1.5 rounded self-start',
          disabled
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        )}
      >
        {label}
      </button>
    </div>
  );
}

export function UpsellSection({ order, onRefresh }: UpsellSectionProps) {
  const [openDialog, setOpenDialog] = useState<UpsellType | null>(null);
  const [nabestelOpen, setNabestelOpen] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, open ? '1' : '0'); } catch { /* ignore */ }
  }, [open]);

  const empty: UpsellStatusEntry = { ordered: false, bron: '' };
  const status = order.upsellStatus;
  const ondertapijt = status?.ondertapijt ?? empty;
  const vlekkenbaas = status?.vlekkenbaas ?? empty;
  const randReparatie = status?.randReparatie ?? empty;

  const orderedCount =
    (ondertapijt.ordered ? 1 : 0) +
    (vlekkenbaas.ordered ? 1 : 0) +
    (randReparatie.ordered ? 1 : 0);

  const terminal = TERMINAL_STATUSES.has(order.status);
  const terminalReason = terminal ? `Upsells niet mogelijk bij status: ${order.status}` : null;
  // Afgeleverde order: ondertapijt kan niet meer met de route mee, maar wél
  // apart verzonden worden (nieuwe webshop-order, aanbiedingsprijs + verzendkosten).
  const delivered = DELIVERED_STATUSES.has(order.status);

  const handleClose = () => setOpenDialog(null);
  const handleAdded = () => {
    setOpenDialog(null);
    onRefresh();
  };

  return (
    <>
      <details
        open={open}
        onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
        className="bg-slate-50/40 rounded-xl border border-slate-200 group"
      >
        <summary className="flex items-center justify-between gap-3 p-4 cursor-pointer list-none select-none">
          <div className="flex items-center gap-2">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Upsell toevoegen
            </h2>
            {orderedCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700">
                {orderedCount}/3 besteld
              </span>
            )}
          </div>
          <svg
            className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <div className="grid gap-3 md:grid-cols-3 px-4 pb-4">
          <UpsellCard
            title="Ondertapijt"
            description={
              delivered
                ? 'Order al afgeleverd — klant wil alsnog een ondertapijt. Apart verzenden via PostNL tegen de aanbiedingsprijs + verzendkosten.'
                : 'Beschermend ondertapijt op maat per kleed. Ook voor een extern kleed dat niet in behandeling is.'
            }
            status={ondertapijt}
            disabled={delivered ? false : terminal}
            disabledReason={terminalReason}
            onClick={() => (delivered ? setNabestelOpen(true) : setOpenDialog('ondertapijt'))}
            orderedLabel="+ Extra ondertapijt"
            ctaLabel={delivered ? 'Nabestellen + verzenden' : undefined}
          />
          <UpsellCard
            title="Vlekkenbaas"
            description="Professionele vlekverwijderaar-set (1–5 stuks)."
            status={vlekkenbaas}
            disabled={terminal || vlekkenbaas.ordered}
            disabledReason={terminalReason}
            onClick={() => setOpenDialog('vlekkenbaas')}
          />
          <UpsellCard
            title="Rand reparatie"
            description="Losse randen opnieuw vastlijmen."
            status={randReparatie}
            disabled={terminal || randReparatie.ordered}
            disabledReason={terminalReason}
            onClick={() => setOpenDialog('rand-reparatie')}
          />
        </div>
      </details>

      {openDialog === 'ondertapijt' && (
        <UpsellOndertapijtDialog orderId={order.id} onClose={handleClose} onAdded={handleAdded} onRefresh={onRefresh} />
      )}
      {openDialog === 'vlekkenbaas' && (
        <UpsellVlekkenbaasDialog orderId={order.id} onClose={handleClose} onAdded={handleAdded} />
      )}
      {openDialog === 'rand-reparatie' && (
        <UpsellRandReparatieDialog orderId={order.id} onClose={handleClose} onAdded={handleAdded} />
      )}
      {nabestelOpen && (
        <NabestelOndertapijtVerzendenDialog
          orderId={order.id}
          onClose={() => setNabestelOpen(false)}
          onDone={onRefresh}
        />
      )}
    </>
  );
}
